import React from "react";
import { Modal, Form, Select, Input, message } from "antd";
import request from "../../../utils/request";
import { ProjectItem, useGlobal } from "../../../contexts/GlobalContext";

const { TextArea } = Input;

interface KnowledgeTextModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  projects: ProjectItem[];
}

const KnowledgeTextModal: React.FC<KnowledgeTextModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  projects,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const { API_BASE_URL, token, user } = useGlobal();

  React.useEffect(() => {
    // 设置默认项目为第一个 PostChat 项目
    if (visible && projects.length > 0) {
      const postChatProjects = projects.filter(project => project.is_postchat);
      if (postChatProjects.length > 0) {
        form.setFieldsValue({ project_id: postChatProjects[0].id });
      }
    }
  }, [visible, projects, form]);

  const handleOk = async () => {
    try {
      // 检查用户是否是 PostChat 会员
      if (!user?.apiInfo?.postchat?.vip_info?.is_vip) {
        message.error("此功能为全功能项目（Pro 及以上）能力");
        return;
      }

      const values = await form.validateFields();
      setLoading(true);

      // 创建文本文件
      const blob = new Blob([values.content], { type: "text/plain" });
      const file = new File([blob], `${values.title}.txt`, {
        type: "text/plain",
      });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("project_id", values.project_id);
      formData.append("title", values.title);

      try {
        const response = await request.post(
          `${API_BASE_URL}/api/v3/knowledge/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.code === 200) {
          message.success("添加成功");
          onCancel();
          form.resetFields();
          onSuccess();
        } else {
          message.error(response.data.msg || "添加失败");
        }
      } catch (error) {
        message.error("添加失败，请重试");
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error("表单验证失败:", error);
    }
  };

  return (
    <Modal
      title="从文本添加"
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="确认添加"
      cancelText="取消"
      confirmLoading={loading}
      width={800}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" style={{ paddingRight: "10px" }}>
        <Form.Item
          name="project_id"
          label="选择项目"
          rules={[{ required: true, message: "请选择项目" }]}
          extra="仅支持 PostChat 类型的项目"
        >
          <Select placeholder="请选择项目">
            {projects.filter(project => project.is_postchat).map((project) => (
              <Select.Option key={project.id} value={project.id}>
                {project.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="title"
          label="知识标题"
          rules={[{ required: true, message: "请输入知识标题" }]}
        >
          <Input placeholder="请输入知识标题" />
        </Form.Item>
        <Form.Item
          name="content"
          label="知识内容"
          rules={[{ required: true, message: "请输入知识内容" }]}
        >
          <TextArea
            rows={12}
            placeholder="请输入知识内容"
            showCount
            maxLength={5000}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default KnowledgeTextModal;
